import Yup from '~/util/yup';
import type { PatientContactModel } from '../types';
import { addPatientContactOptions, editPatientContactOptions, getPatientContactsOptions, RelationshipOptions } from '@generated';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { CheckboxGroupFormField } from '~/shared/forms/controls/fields/CheckboxGroupFormField';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { useQuery } from '@tanstack/react-query';
import { FormModal } from '~/shared/forms/FormModal';
import type { FormikValues } from 'formik';

const validationSchema = Yup.object().shape({
	firstName: Yup.string().label('First Name').max(255, 'First name must be 255 characters or less.').required().nullable(),
	middleInitial: Yup.string().label('M.I.').max(1, 'M.I. must be 1 character or less.').nullable(),
	lastName: Yup.string().label('Last Name').max(255, 'Last name must be 255 characters or less.').required().nullable(),
	mainPhoneNumber: Yup.string().label('Mobile Phone #').nullable().required(),
	alternatePhoneNumber: Yup.string().label('Alternate Phone #').nullable(),
	emailAddress: Yup.string().label('Email Address').nullable(),
	relationshipId: Yup.string().label('Relationship To Patient').nullable().required(),
	primaryContact: Yup.bool().label('Primary Contact').nullable(),
	caregiver: Yup.bool().label('Caregiver').nullable(),
	emergencyContact: Yup.bool().label('Emergency Contact').nullable(),
	scheduleAvailability: Yup.string().label('Schedule/Availability').nullable(),
	livesWithPatient: Yup.bool().label('Lives With Patient').nullable(),
	address: Yup.object()
		.address()
		.label('Contact Address')
		.nullable()
		.when('livesWithPatient', {
			is: false,
			then: schema => schema.required('Contact address is required when lives with patient is not checked.')
		}),
	notes: Yup.string().label('Notes').nullable()
});

export function ContactFormModal({
	stayId,
	existingContact,
	closeModal
}: {
	stayId: string;
	existingContact?: PatientContactModel;
	closeModal: () => void;
}) {
	const { data: patientContacts, isLoading } = useQuery(getPatientContactsOptions(stayId));

	if (isLoading || !patientContacts) {
		return null;
	}

	const contactTypes = [];
	if (existingContact?.primaryContact) {
		contactTypes.push(1);
	}
	if (existingContact?.caregiver) {
		contactTypes.push(2);
	}
	if (existingContact?.emergencyContact) {
		contactTypes.push(3);
	}

	const initialValues = existingContact
		? {
				...existingContact,
				patientContactId: existingContact.id,
				contactTypes: contactTypes
		  }
		: {
				stayId,
				firstName: null,
				lastName: null,
				mainPhoneNumber: null,
				alternatePhoneNumber: null,
				emailAddress: null,
				relationshipId: null,
				address: null,
				contactTypes: [],
				livesWithPatient: false,
				scheduleAvailability: null
		  };

	return (
		<FormModal
			title={`${existingContact ? 'Edit' : 'Add'} Contact`}
			initialValues={initialValues}
			saveMutation={{
				...(existingContact ? editPatientContactOptions : addPatientContactOptions),
				invalidateQueryKeys: [getPatientContactsOptions(stayId).queryKey]
			}}
			onSaveTransform={(values: FormikValues) => {
				return {
					...values,
					primaryContact: values.contactTypes.includes('1'),
					caregiver: values.contactTypes.includes('2'),
					emergencyContact: values.contactTypes.includes('3')
				};
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
			showValues
		>
			{formHelpers => {
				return (
					<div className="grid grid-cols-1 gap-4">
						<div className="space-y-6">
							<div className="space-y-4">
								<div className="grid grid-cols-1 gap-4 md:grid-cols-6">
									<div className="md:col-span-2">
										<TextFormField name="firstName" />
									</div>
									<div className="md:col-span-1">
										<TextFormField name="middleInitial" />
									</div>
									<div className="md:col-span-3">
										<TextFormField name="lastName" />
									</div>
								</div>
							</div>
							<div className="space-y-4">
								<div className="grid grid-cols-3 gap-4">
									<TextFormField name="mainPhoneNumber" />
									<TextFormField name="alternatePhoneNumber" />
									<TextFormField name="emailAddress" />
								</div>
							</div>
							<div className="space-y-4">
								<div className="grid grid-cols-1 gap-6">
									<SelectFormField name="relationshipId" options={RelationshipOptions} />
									<CheckboxGroupFormField
										name="contactTypes"
										label="Contact Types"
										options={[
											{ id: 1, label: 'Primary Contact' },
											{ id: 2, label: 'Caregiver' },
											{ id: 3, label: 'Emergency Contact' }
										]}
									/>

									<TextAreaFormField name="scheduleAvailability" />
								</div>
							</div>
						</div>
						<div className="space-y-6">
							<div className="space-y-4">
								<CheckboxFormField name="livesWithPatient" hideLabel className="mb-2" />
								{!formHelpers?.values?.livesWithPatient && <AddressFormField name="address" />}

								<TextAreaFormField name="notes" />
							</div>

							<div className="space-y-2" />
						</div>
					</div>
				);
			}}
		</FormModal>
	);
}
